import {useRef, type MouseEvent} from "react";
import {ChevronRightIcon} from "@heroicons/react/24/outline";
import Checkbox from "../../../../general/Checkbox.tsx";
import {MoneyLabel} from "../../../../general/MoneyLabel.tsx";
import {toDecimalFromMoney, type Money} from "../../../../../lib/Money.ts";
import {cn, formatDate} from "../../../../../lib/utils.ts";
import type {PanelBooking} from "../types.ts";
import {TypeBadge} from "./TypeBadge.tsx";
import {NO_SUBLEDGER_LABEL} from "../../../SubledgerSelector";

const WRAPPING_TEXT = "whitespace-normal [overflow-wrap:anywhere]";

interface BookingRowProps {
    booking: PanelBooking;
    subledgerSplit: boolean;
    selected: boolean;
    expanded: boolean;
    tabIndex: 0 | -1;
    onToggleExpand: () => void;
    onToggleSelect: (shiftKey: boolean) => void;
    onFocus: () => void;
}

function AmountCell({amount}: {amount: Money}) {
    if (amount.signed_amount === 0) return <span className="text-muted-foreground">-</span>;
    return (
        <span className="text-gray-800 tabular-nums">
            <MoneyLabel value={toDecimalFromMoney(amount)} currency={amount.currency} showCurrency={true} />
        </span>
    );
}

export function BookingRow({
    booking,
    subledgerSplit,
    selected,
    expanded,
    tabIndex,
    onToggleExpand,
    onToggleSelect,
    onFocus,
}: BookingRowProps) {
    const shiftKeyRef = useRef(false);

    function handleCheckboxMouseDown(event: MouseEvent) {
        shiftKeyRef.current = event.shiftKey;
        event.stopPropagation();
    }

    function handleCheckboxChange() {
        if (!booking.selectable) return;
        onToggleSelect(shiftKeyRef.current);
        shiftKeyRef.current = false;
    }

    function handleRowClick(event: MouseEvent) {
        const selectsInstead = event.shiftKey || event.metaKey || event.ctrlKey;
        if (!selectsInstead) {
            onToggleExpand();
            return;
        }
        if (!booking.selectable) return;
        event.preventDefault();
        onToggleSelect(event.shiftKey);
    }

    function handleCheckboxCellClick(event: MouseEvent) {
        event.stopPropagation();
        if (!booking.selectable) return;
        if ((event.target as HTMLElement).closest("input")) return;
        onToggleSelect(event.shiftKey);
    }

    return (
        <tr
            data-row-key={booking.key}
            role="row"
            aria-expanded={expanded}
            aria-selected={selected}
            tabIndex={tabIndex}
            onFocus={onFocus}
            onClick={handleRowClick}
            className={cn(
                "group cursor-pointer border-b border-border align-top hover:bg-muted",
                "focus:outline-none",
                selected && "bg-superliteprimary",
            )}
        >
            <td
                className="relative w-14 py-3 pl-4 pr-2"
                onMouseDown={handleCheckboxMouseDown}
                onClick={handleCheckboxCellClick}
            >
                <span
                    aria-hidden
                    className="pointer-events-none absolute inset-y-0 left-0 w-0.5 bg-primary opacity-0 group-focus-visible:opacity-100"
                />
                <span
                    className="flex justify-center"
                    title={booking.notSelectableReason ?? undefined}
                >
                    <Checkbox
                        value={selected}
                        disabled={!booking.selectable}
                        onUpdateFunc={handleCheckboxChange}
                        ariaLabel={`Selecteer ${booking.title}`}
                    />
                </span>
            </td>
            <td className="w-8 px-1 py-3">
                <ChevronRightIcon
                    className={cn(
                        "size-4 text-muted-foreground transition-transform motion-reduce:transition-none",
                        expanded && "rotate-90",
                    )}
                    aria-hidden
                />
            </td>
            <td className="px-2 py-3">
                <TypeBadge kind={booking.kind} fileName={booking.fileName} />
            </td>
            <td className="px-2 py-3">
                <p className={cn("text-sm text-gray-800", WRAPPING_TEXT)}>{booking.title}</p>
                {booking.subtitle && (
                    <p className={cn("mt-0.5 text-xs text-muted-foreground", WRAPPING_TEXT)}>{booking.subtitle}</p>
                )}
                {subledgerSplit && (
                    <p className={cn("mt-0.5 text-xs text-muted-foreground", WRAPPING_TEXT)}>
                        {booking.subledger ? booking.subledger.name : NO_SUBLEDGER_LABEL}
                    </p>
                )}
            </td>
            <td className="whitespace-nowrap px-2 py-3 text-sm text-gray-800 tabular-nums">
                {formatDate(booking.date) || "-"}
            </td>
            <td className="whitespace-nowrap px-2 py-3 text-right text-sm">
                <AmountCell amount={booking.debit} />
            </td>
            <td className="whitespace-nowrap px-2 py-3 text-right text-sm">
                <AmountCell amount={booking.credit} />
            </td>
            <td className="whitespace-nowrap py-3 pl-2 pr-4 text-sm text-gray-800 tabular-nums">
                {booking.vatLabel ?? <span className="text-muted-foreground">-</span>}
            </td>
        </tr>
    );
}
